import { getSizeConfig } from './sizeConfig/SizeConfigFactory';
import { getFileNameFromPath } from '../file';
import { getDefaultFontProperties } from './constants';
import { SizeConfig } from './sizeConfig/SizeConfig';
import { AssetConfigCaptionStyle, CaptionStyle, Clip, ClipMetadata, SizeType } from '@/domains/asset';
import { Word } from '@/domains/transcript';
import { getFontByLocation } from '@/Pages/Clip/SideBar/FontSelector/constants';

export function getCaptions(introVideoDuration: number, sizeConfig: ReturnType<typeof getSizeConfig>, clip: Clip) {
  const {
    top: bottom_margin,
    left: left_margin,
    right: right_margin,
    rotation,
    wrap_style,
    alignment
  } = sizeConfig.getCaptionAttributes({
    containerHeight: sizeConfig.getHeight(),
    containerWidth: sizeConfig.getWidth(),
    captionFontSize: 0
  });

  return clip.asset_metadata.layout === 'AUDIOGRAM'
    ? [
        {
          words: getCaptionWords(clip.asset_metadata),
          drift: introVideoDuration,
          bottom_margin,
          left_margin,
          right_margin,
          rotation,
          wrap_style,
          style: getFontStyles(clip.asset_metadata, sizeConfig, alignment)
        }
      ]
    : getVideoLayoutCaptions(clip, introVideoDuration, sizeConfig);
}

function getVideoLayoutCaptions(clip: Clip, introVideoDuration: number, sizeConfig: SizeConfig) {
  if (!clip.asset_metadata.caption_positions) {
    return [
      {
        words: getCaptionWords(clip.asset_metadata),
        drift: introVideoDuration,
        left_margin: sizeConfig.getCaptionsLeftMargin(),
        right_margin: sizeConfig.getCaptionsRightMargin(),
        bottom_margin: sizeConfig.getCaptionsBottomMargin(),
        rotation: 0,
        style: getFontStyles(clip.asset_metadata, sizeConfig)
      }
    ];
  }
  const captionsScale = clip.asset_metadata.caption_styles?.scale || 1;
  const fontItem = getFontByLocation(
    clip.asset_metadata.caption_styles?.font_location || clip.asset_metadata.font_location
  );
  const fontScaleInPx = fontItem?.scaleInPx || 0;
  const yCorrection = fontScaleInPx ? fontScaleInPx * 2 * captionsScale : 0;
  const captionsX = parseFloat(clip.asset_metadata.caption_positions!.x as unknown as string);
  const captionsWidth = parseFloat(clip.asset_metadata.caption_positions!.width as unknown as string);
  const captionsY = parseFloat(clip.asset_metadata.caption_positions!.y as unknown as string);

  return [
    {
      words: getCaptionWords(clip.asset_metadata),
      drift: introVideoDuration,
      left_margin: (sizeConfig.getWidth() / 100) * captionsX || 0,
      right_margin:
        sizeConfig.getWidth() -
          ((sizeConfig.getWidth() / 100) * captionsX + (sizeConfig.getWidth() / 100) * captionsWidth) || 0,
      bottom_margin:
        (sizeConfig.getHeight() / 100) * (clip.asset_metadata.caption_positions?.textY || captionsY || 0) - yCorrection,
      rotation: 0,
      wrap_style: 1,
      style: getFontStyles(clip.asset_metadata, sizeConfig, 8)
    }
  ];
}

function getCaptionWords(clipMetadata: ClipMetadata): Word[] {
  if (!clipMetadata.caption_styles?.caps) {
    return clipMetadata.captions;
  }

  return clipMetadata.captions.map(w => {
    return {
      ...w,
      content: w.content.toUpperCase()
    };
  });
}

function getFontStyles(
  clipMetadata: ClipMetadata,
  sizeConfig: ReturnType<typeof getSizeConfig>,
  // alignment values are based on keyboard numpad directions
  alignment: number = 2
): AssetConfigCaptionStyle {
  const captionsScale = clipMetadata.caption_styles?.scale || 1;
  const fontItem = getFontByLocation(clipMetadata.font_location);
  return {
    highlight_style: {
      ...getHighlightStyleBackground(clipMetadata),
      font_color:
        clipMetadata.highlight_type !== 'none' && !!clipMetadata.text_highlight_color
          ? clipMetadata.text_highlight_color
          : clipMetadata.magicLayout?.textColor,
      font_size: Math.round(
        getFontSize({
          playerHeight: sizeConfig.getHeight(),
          clipSize: clipMetadata.size,
          scale: captionsScale
        }) * (fontItem?.scale ? fontItem.scale / 100 : 1)
      ),
      font_opacity: '00',
      alignment,
      bold: 1,
      ...getFontProperties(clipMetadata)
    },
    regular_style: {
      font_color: clipMetadata.magicLayout?.textColor,
      font_size: Math.round(
        getFontSize({
          playerHeight: sizeConfig.getHeight(),
          clipSize: clipMetadata.size,
          scale: captionsScale
        }) * (fontItem?.scale ? fontItem.scale / 100 : 1)
      ),
      font_opacity: clipMetadata.highlight_type === 'text' ? '80' : '00',
      alignment,
      bold: 1,
      ...getFontProperties(clipMetadata),
      ...(clipMetadata.caption_styles?.animation === 'Background'
        ? {
            background_color: clipMetadata.caption_styles.background_style_color,
            background_opacity: '66',
            border_style: 4,
            shadow: 0,
            outline: 0
          }
        : getFontAnimation(clipMetadata))
    }
  };
}

function getFontAnimation(clipMetadata: ClipMetadata): {
  border_style: number;
  outline: number;
  shadow: number;
  font_opacity?: string;
} {
  const animation = clipMetadata.caption_styles?.animation;
  if (!animation || animation === CaptionStyle.Basic) {
    // border_style: 1, outline: 0, shadow:0 will make background transparent
    return {
      border_style: 1,
      shadow: 0,
      outline: 0
    };
  }

  if (animation === CaptionStyle.Outline) {
    return {
      border_style: 1,
      shadow: 0,
      outline: 2,
      font_opacity: clipMetadata.highlight_type === 'text' ? '80' : '00'
    };
  }
  return {
    border_style: 1,
    shadow: 0,
    outline: 0
  };
}

function getHighlightStyleBackground(clipMetadata: ClipMetadata) {
  // If it's not box - return transparent background
  if (clipMetadata.highlight_type === 'box') {
    return {
      background_color: clipMetadata.word_highlight_color
    };
  }
  if (clipMetadata.caption_styles?.animation === 'Background') {
    return {
      background_color: clipMetadata.caption_styles.background_style_color,
      border_style: 4,
      shadow: 0,
      outline: 0
    };
  }
  return getFontAnimation(clipMetadata);
}

export function getFontProperties(
  clipMetadata: ClipMetadata,
  isRegular: boolean = true
): { font: string; font_location: string } {
  return clipMetadata.font_location
    ? {
        font: getFileNameFromPath(clipMetadata.font_location),
        font_location: clipMetadata.font_location
      }
    : getDefaultFontProperties(isRegular);
}

export function getFontSize({
  playerHeight,
  clipSize,
  scale
}: {
  playerHeight: number;
  clipSize: SizeType;
  scale: number;
}) {
  return Math.round((clipSize === 'PORTRAIT' ? (48 / 1280) * playerHeight : (32 / 720) * playerHeight) * scale);
}
